import { Button } from "@/components/ui/Button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { capture } from "@/lib/analytics/posthog";

const CallToAction = () => {
    const { ref, isVisible } = useScrollAnimation();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Pass email through to the signup form so we don't lose it
        const trimmed = email.trim();
        capture("cta_clicked", { cta: "start_fund_page", location: "call_to_action", with_email: Boolean(trimmed) });
        if (trimmed) {
            navigate(`/signup-creator?email=${encodeURIComponent(trimmed)}`);
        } else {
            navigate("/signup-creator");
        }
    };

    return (
        <section
            ref={ref}
            className="relative py-24 md:py-28 overflow-hidden isolate"
        >
            {/* Background */}
            <div
                aria-hidden
                className="absolute inset-0 -z-10"
                style={{
                    background:
                        "radial-gradient(600px 400px at 85% 15%, hsl(var(--gold) / 0.55), transparent 60%), radial-gradient(700px 450px at 10% 110%, hsl(var(--secondary) / 0.40), transparent 60%), linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(210 80% 14%) 100%)",
                }}
            />
            <div
                aria-hidden
                className="hero-blob absolute -top-20 right-1/4 w-[380px] h-[380px] rounded-full blur-3xl opacity-30 bg-white"
                style={{ animation: "blobFloat2 20s ease-in-out infinite" }}
            />

            <div
                className={`max-w-4xl mx-auto px-6 text-center transition-all duration-700 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
            >
                <h2 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight drop-shadow-sm">
                    Your education is worth
                    <br />
                    <span
                        className="bg-clip-text text-transparent"
                        style={{
                            backgroundImage:
                                "linear-gradient(90deg, hsl(var(--gold)) 0%, #FFE9A8 100%)",
                        }}
                    >
                        investing in.
                    </span>
                </h2>
                <p className="text-lg md:text-xl text-white/85 mt-5 max-w-2xl mx-auto leading-relaxed">
                    Drop your email and we'll spin up your Fund Page in 60 seconds. Free, no card,
                    pause or delete any time.
                </p>

                <form
                    onSubmit={handleSubmit}
                    className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto p-1.5 sm:rounded-full sm:bg-white/10 sm:backdrop-blur sm:ring-1 sm:ring-white/20"
                >
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@school.edu"
                        className="flex-1 px-6 py-4 rounded-full bg-white text-primary placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-white/60 text-base"
                        aria-label="Email"
                    />
                    <Button
                        type="submit"
                        size="lg"
                        className="bg-white text-primary hover:bg-white/90 rounded-full font-semibold text-base px-7 py-6 group"
                    >
                        Start my Fund Page
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </form>

                <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-white/75 text-sm">
                    <span className="inline-flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4" /> Stripe-verified payouts
                    </span>
                    <span className="hidden md:inline text-white/30">·</span>
                    <span>Free for students</span>
                    <span className="hidden md:inline text-white/30">·</span>
                    <span>Cancel any time</span>
                </div>
            </div>
        </section>
    );
};

export default CallToAction;
